import React, { useState, useEffect, useRef, useCallback } from "react";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import {
  Block,
  BlockBetween,
  BlockDes,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
} from "../../../components/Component";
import { Card } from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import { Table, Empty, Input, Select, Drawer, Badge as AntBadge, Tooltip } from "antd";
import { SearchOutlined, ReloadOutlined, FilterOutlined, CheckCircleOutlined } from "@ant-design/icons";
import moment from "moment";
import Swal from "sweetalert2";
import superAdminService from "../../../api/super-admin/superAdminService";
import type { AppListItem } from "../../../types/super-admin";

function useDebounce(value: string, delay: number) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}

const AppList: React.FC = () => {
  const navigate = useNavigate();
  const [onSearchText, setSearchText] = useState("");
  const debouncedSearch = useDebounce(onSearchText, 300);

  // Filter state
  const [filterStatus, setFilterStatus] = useState<string | undefined>(undefined);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
    sortOrder: "DESC" as "ASC" | "DESC",
    direction: "P" as "P" | "F" | "B",
    pageDiff: 0,
    paginationId: null as number | null,
  });

  const [dataSource, setDataSource] = useState<AppListItem[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchData = async (params: any = {}) => {
    setLoading(true);
    try {
      const apiParams: any = {
        sortOrder: params.sortOrder || pagination.sortOrder,
        direction: params.direction || pagination.direction,
        pageSize: params.pageSize || pagination.pageSize,
        pageDiff: params.pageDiff ?? pagination.pageDiff,
        paginationId: params.paginationId ?? pagination.paginationId,
        searchQuery:
          params.searchQuery !== undefined
            ? params.searchQuery
            : onSearchText,
      };

      const { data, status, message } =
        await superAdminService.getApplicationList(apiParams);

      if (status) {
        const list = Array.isArray(data) ? data : data?.list || [];
        const count = Array.isArray(data) ? data.length : data?.count || 0;
        setDataSource(list);
        setPagination((prev) => ({
          ...prev,
          total: count,
          ...(params.current && { current: params.current }),
          ...(params.pageSize && { pageSize: params.pageSize }),
          ...(params.sortOrder && { sortOrder: params.sortOrder }),
          ...(params.direction && { direction: params.direction }),
          ...(params.pageDiff !== undefined && { pageDiff: params.pageDiff }),
          ...(params.paginationId !== undefined && {
            paginationId: params.paginationId,
          }),
        }));
      } else {
        Swal.fire("Error", message, "error");
      }
    } catch (error: any) {
      Swal.fire("Error", error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const hasFetched = useRef(false);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchData({
      current: 1,
      pageDiff: 0,
      paginationId: null,
      direction: "P",
    });
  }, []);

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    fetchData({
      current: 1,
      pageDiff: 0,
      paginationId: null,
      direction: "P",
      searchQuery: debouncedSearch.trim(),
    });
  }, [debouncedSearch]);

  const tableOnChange = async (
    newPagination: any,
    _filters: any,
    _sorter: any
  ) => {
    const apiParams: any = {
      current: newPagination.current,
      pageSize: newPagination.pageSize,
      pageDiff: 0,
      paginationId: null,
      direction: "P",
    };

    if (newPagination.pageSize !== pagination.pageSize) {
      apiParams.pageDiff = Math.floor(
        ((pagination.current - 1) * pagination.pageSize) /
          newPagination.pageSize
      );
    } else if (newPagination.current !== pagination.current) {
      apiParams.direction =
        newPagination.current > pagination.current ? "F" : "B";
      apiParams.pageDiff =
        Math.abs(newPagination.current - pagination.current) - 1;

      if (apiParams.direction === "F" && dataSource.length > 0) {
        apiParams.paginationId =
          dataSource[dataSource.length - 1]?.paginationId || dataSource[dataSource.length - 1]?.appId;
      } else if (apiParams.direction === "B" && dataSource.length > 0) {
        apiParams.paginationId = dataSource[0]?.paginationId || dataSource[0]?.appId;
      }
    }

    await fetchData(apiParams);
  };

  const filteredData = filterStatus
    ? dataSource.filter((item) => item.status === filterStatus)
    : dataSource;

  const activeFilterCount = [filterStatus].filter(Boolean).length;

  const columns = [
    {
      title: "App Code",
      dataIndex: "appCode",
      key: "appCode",
      width: 120,
      render: (text: string | null, record: AppListItem) => (
        <Link
          to={`/sa-applications/${record.appId}`}
          className="text-primary fw-bold"
        >
          {text || "-"}
        </Link>
      ),
    },
    {
      title: "Application Name",
      dataIndex: "appName",
      key: "appName",
      width: 250,
      render: (text: string, record: AppListItem) => (
        <Link
          to={`/sa-applications/${record.appId}`}
          className="text-primary"
        >
          {text}
        </Link>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 300,
      render: (text: string | null) => text || "-",
    },
    {
      title: "Modules",
      dataIndex: "moduleCount",
      key: "moduleCount",
      width: 100,
      render: (val: number) => val || 0,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status: string) => (
        <span
          className={`tb-status text-${status === "Active" ? "success" : "danger"}`}
        >
          {status}
        </span>
      ),
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 130,
      render: (date: string) => moment(date).format("DD MMM YYYY"),
    },
  ];

  return (
    <React.Fragment>
      <Head title="Application Management" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle tag="h3" page>
                Application Management
              </BlockTitle>
              <BlockDes className="text-soft">
                <p>You have total {pagination.total} applications.</p>
              </BlockDes>
            </BlockHeadContent>
            <BlockHeadContent>
              <Button
                color="primary"
                onClick={() => navigate("/sa-applications/create")}
              >
                <Icon name="plus"></Icon>
                <span>Add</span>
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <Card className="card-bordered">
            <div className="card-inner py-3">
              <div className="d-flex justify-content-between align-items-center">
                <Input
                  prefix={<SearchOutlined style={{ color: "#b0b0b0" }} />}
                  placeholder="Search by app code, name..."
                  value={onSearchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  variant="borderless"
                  style={{ width: 570, borderBottom: "1.5px solid #e0e0e0", borderRadius: 0 }}
                  allowClear
                />
                <div className="d-flex align-items-center" style={{ gap: 4 }}>
                  <Tooltip title="Refresh">
                    <span
                      onClick={() => {
                        hasFetched.current = false;
                        fetchData({ current: 1, pageDiff: 0, paginationId: null, direction: "P" });
                      }}
                      style={{
                        display: "inline-flex", alignItems: "center", justifyContent: "center",
                        width: 36, height: 36, borderRadius: "50%", cursor: "pointer",
                        transition: "background 0.2s",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f0f0")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                    >
                      <ReloadOutlined style={{ fontSize: 16, color: "#555" }} />
                    </span>
                  </Tooltip>
                  <Tooltip title="Filters">
                    <AntBadge count={activeFilterCount} size="small" offset={[-4, 4]}>
                      <span
                        onClick={() => setFilterDrawerOpen(true)}
                        style={{
                          display: "inline-flex", alignItems: "center", justifyContent: "center",
                          width: 36, height: 36, borderRadius: "50%", cursor: "pointer",
                          transition: "background 0.2s",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f0f0")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <FilterOutlined style={{ fontSize: 16, color: "#555" }} />
                      </span>
                    </AntBadge>
                  </Tooltip>
                </div>
              </div>
            </div>

            <div className="card-inner p-0">
              {loading ? (
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: "#fafafa", borderBottom: "1px solid #f0f0f0" }}>
                      {["App Code", "Application Name", "Description", "Modules", "Status", "Created"].map((h) => (
                        <th key={h} style={{ padding: "11px 12px", fontSize: 12, fontWeight: 600, color: "#8c8c8c", textAlign: "left", whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...Array(10)].map((_, i) => (
                      <tr key={i} style={{ borderBottom: "1px solid #f5f5f5" }}>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 80, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 180, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 220, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 40, height: 14 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 55, height: 22, borderRadius: 4 }} /></td>
                        <td style={{ padding: "12px" }}><div className="skeleton-bar" style={{ width: 85, height: 14 }} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : filteredData.length > 0 ? (
                <Table
                  columns={columns}
                  dataSource={filteredData}
                  rowKey="appId"
                  pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '15', '20', '50', '100', '500'],
                  }}
                  onChange={tableOnChange}
                  scroll={{ x: 1000  }}
                  bordered
                  size="small"
                />
              ) : (
                <Empty description="No applications found" />
              )}
            </div>
          </Card>
        </Block>
      </Content>

      <Drawer
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <FilterOutlined style={{ fontSize: 16, color: "#6576ff" }} />
            <span style={{ fontSize: 16, fontWeight: 700 }}>Filters</span>
            {activeFilterCount > 0 && (
              <AntBadge count={activeFilterCount} style={{ backgroundColor: "#6576ff", marginLeft: 4 }} />
            )}
          </div>
        }
        placement="right"
        onClose={() => setFilterDrawerOpen(false)}
        open={filterDrawerOpen}
        width={450}
        zIndex={1060}
        extra={
          filterStatus ? (
            <span style={{ fontSize: 13, color: "#ff4d4f", cursor: "pointer", fontWeight: 600 }} onClick={() => setFilterStatus(undefined)}>
              Reset All
            </span>
          ) : null
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
          <div style={{
            padding: "14px 16px", borderRadius: 10,
            background: "linear-gradient(135deg, #f0f5ff 0%, #e6f7ff 100%)",
            border: "1px solid #d6e4ff",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <CheckCircleOutlined style={{ color: "#1890ff", fontSize: 15 }} />
              <span style={{ fontWeight: 700, fontSize: 13, color: "#1d1d1d" }}>Status</span>
            </div>
            <Select
              placeholder="All statuses"
              allowClear
              style={{ width: "100%" }}
              value={filterStatus}
              onChange={(val) => setFilterStatus(val)}
              options={[
                { label: "Active", value: "Active" },
                { label: "Inactive", value: "Inactive" },
              ]}
            />
          </div>
        </div>
      </Drawer>
    </React.Fragment>
  );
};

export default AppList;
